import CodeBlock from '../CodeBlock';

export default function DocsConfigModule() {
  return (
    <section data-docs-section="config" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#config" className="hover:text-sky-400 transition-colors">Config</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-4">
        Immutable configuration with a four-level resolution order: explicit
        arguments → environment variables → config file → defaults. On-platform,
        token and API URL are injected automatically.
      </p>

      <div className="font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-4 text-gray-300 overflow-x-auto">
        from propalgos.config import get_config, Config
      </div>

      <h3 className="text-lg font-semibold text-white mt-6 mb-3">Config Dataclass</h3>
      <div className="overflow-x-auto mb-6">
        <table className="text-sm text-left w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="py-2 pr-4 text-gray-400 font-medium">Field</th>
              <th className="py-2 pr-4 text-gray-400 font-medium">Type</th>
              <th className="py-2 pr-4 text-gray-400 font-medium">Default</th>
              <th className="py-2 text-gray-400 font-medium">Description</th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">api_url</td>
              <td className="py-2 pr-4 text-gray-500">str</td>
              <td className="py-2 pr-4 text-gray-600 font-mono text-xs">https://api.propalgos.ai</td>
              <td className="py-2 text-gray-400">PropAlgos API base URL</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">token</td>
              <td className="py-2 pr-4 text-gray-500">str | None</td>
              <td className="py-2 pr-4 text-gray-600 font-mono text-xs">None</td>
              <td className="py-2 text-gray-400">API authentication token</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">environment</td>
              <td className="py-2 pr-4 text-gray-500">str</td>
              <td className="py-2 pr-4 text-gray-600 font-mono text-xs">"production"</td>
              <td className="py-2 text-gray-400">Environment name</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">cache_dir</td>
              <td className="py-2 pr-4 text-gray-500">Path</td>
              <td className="py-2 pr-4 text-gray-600 font-mono text-xs">~/.cache/propalgos</td>
              <td className="py-2 text-gray-400">Local cache directory</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-sky-400">config_dir</td>
              <td className="py-2 pr-4 text-gray-500">Path</td>
              <td className="py-2 pr-4 text-gray-600 font-mono text-xs">~/.config/propalgos</td>
              <td className="py-2 text-gray-400">Config file directory</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 className="text-base font-semibold text-white mb-2">Properties</h4>
      <div className="space-y-1 mb-6 text-sm">
        <div>
          <code className="text-sky-400">config.is_on_platform</code>
          <span className="text-gray-500"> → bool</span>
          <span className="text-gray-400 ml-2">— True when PROPALGOS_INSTANCE_ID is set</span>
        </div>
        <div>
          <code className="text-sky-400">config.instance_id</code>
          <span className="text-gray-500"> → str | None</span>
        </div>
        <div>
          <code className="text-sky-400">config.user_id</code>
          <span className="text-gray-500"> → str | None</span>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-white mt-6 mb-3">Environment Variables</h3>
      <div className="overflow-x-auto mb-4">
        <table className="text-sm text-left w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="py-2 pr-4 text-gray-400 font-medium">Variable</th>
              <th className="py-2 text-gray-400 font-medium">Maps to</th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            {[
              ['PROPALGOS_API_URL', 'config.api_url'],
              ['PROPALGOS_BACKEND_URL', 'config.api_url (fallback)'],
              ['PROPALGOS_TOKEN', 'config.token'],
              ['PROPALGOS_ENV', 'config.environment'],
              ['PROPALGOS_CACHE_DIR', 'config.cache_dir'],
              ['PROPALGOS_INSTANCE_ID', 'config.instance_id (platform detection)'],
              ['PROPALGOS_USER_ID', 'config.user_id'],
              ['PROPALGOS_DATASETS_DIR', 'datasets base directory'],
            ].map(([env, desc]) => (
              <tr key={env} className="border-b border-gray-800/50 last:border-0">
                <td className="py-1.5 pr-4 font-mono text-sky-400 text-xs">{env}</td>
                <td className="py-1.5 text-gray-400">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CodeBlock
        code={`from propalgos.config import get_config

config = get_config()
print(config.api_url)        # "https://api.propalgos.ai"
print(config.is_on_platform) # True if inside managed instance`}
      />
    </section>
  );
}
